/**
 * A presigned link to a report payload (reporting-api `GET /v1/reports/{id}/data`, then S3 or its CDN):
 * `reports/{id}/data.json`, or a quote-free copy under `reports/{id}/redacted/`. It reads the payload
 * for as long as it is valid, quotes included, so no telemetry SDK (Datadog RUM, Datadog Logs, Sentry)
 * may ship it (CORE-569).
 */
export const isReportDataSignedUrl = (url: string | undefined): boolean => {
  if (!url) return false;

  try {
    const { pathname, searchParams } = new URL(url);

    return (
      // Unanchored, so a path-style URL (`/{bucket}/reports/…`) matches as well as the CDN's.
      searchParams.has('X-Amz-Signature') && /\/reports\/[^/]+\/(data\.json$|redacted\/)/.test(pathname)
    );
  } catch {
    return false;
  }
};

const URL_IN_TEXT = /https?:\/\/[^\s"'<>]+/g;

/** Whether free text (a log message, a span name) quotes a signed report payload link. */
export const containsReportDataSignedUrl = (text: string | undefined): boolean =>
  !!text && (text.match(URL_IN_TEXT) ?? []).some(isReportDataSignedUrl);

/** The link without its query (and so without the signature), or the input unchanged when it is not one. */
export const stripReportDataSignature = (url: string): string => {
  if (!isReportDataSignedUrl(url)) return url;

  const parsed = new URL(url);

  return `${parsed.origin}${parsed.pathname}`;
};
